import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  FiArrowLeft,
  FiSave,
  FiX,
  FiPackage,
  FiClock,
  FiUser,
  FiCalendar,
  FiLayers,
  FiCheckSquare
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { procesosService, productosService, clientesService } from '../../services';
import { toLocalDateString } from '../../utils';

const ProcesoForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditing = Boolean(id);

  // Estados principales
  const [processType, setProcessType] = useState('');
  const [formData, setFormData] = useState({
    production_process: {
      process_type: '',
      process_date: toLocalDateString(),
      batch_number: '',
      product_id: null,
      product_name: '',
      operator_name: '',
      supervisor_id: null,
      quantity_produced: 0,
      quantity_units: 'unidades',
      start_time: '',
      end_time: '',
      duration_minutes: 0,
      notes: ''
    },
    process_details: {}
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Listas para selects
  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]);

  const processTypes = procesosService.getProcessTypes();
  const processStatuses = procesosService.getProcessStatuses();
  const qualityStatuses = procesosService.getQualityStatuses();

  // Capturar tipo de proceso desde navegación
  useEffect(() => {
    if (location.state?.processType) {
      setProcessType(location.state.processType);
    }
  }, [location.state]);

  useEffect(() => {
    loadProducts();
    loadClients();
    if (isEditing && id) {
      loadProceso();
    }
  }, [id, isEditing]);

  // Actualizar campos según tipo de proceso
  useEffect(() => {
    if (processType) {
      initializeProcessDetails(processType);
    }
  }, [processType]);

  const loadProducts = async () => {
    try {
      const result = await productosService.getProductos({ limit: 1000 });
      if (result.success) {
        setProductos(result.data || []);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadClients = async () => {
    try {
      const result = await clientesService.getClientes({ limit: 1000 });
      if (result.success) {
        setClientes(result.data || []);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadProceso = async () => {
    setIsLoading(true);
    try {
      const result = await procesosService.getProceso(id);
      if (result.success) {
        setProcessType(result.data.process_type);
        setFormData({
          production_process: result.data,
          process_details: result.data.process_details || {}
        });
      } else {
        setError(result.message || 'Error al cargar proceso');
      }
    } catch (error) {
      setError('Error de conexión al cargar proceso');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const initializeProcessDetails = (type) => {
    let details = {};

    switch (type) {
      case 'ENCAPSULATION':
        details = {
          net_weight_per_capsule: 0,
          actual_weight_per_capsule: 0,
          bottles_produced: 0,
          capsules_per_bottle: 0,
          total_capsules: 0,
          waste_percentage: 0,
          efficiency_percentage: 100,
          capsule_appearance: '',
          weight_variance: 0
        };
        break;

      case 'TEA_PACKING':
        details = {
          packed_units: 0,
          tea_bags_per_box: 0,
          total_tea_bags: 0,
          packer_name: '',
          company_name: 'Betel Natural',
          seal_quality: '',
          package_appearance: ''
        };
        break;

      case 'PACKAGING':
        details = {
          packed_units: 0,
          units_per_container: 0,
          total_containers: 0,
          packer_name: '',
          packaging_type: '',
          package_condition: '',
          labeling_quality: ''
        };
        break;

      case 'CLEANING':
        details = {
          production_area: false,
          packaging_area: false,
          warehouse_area: false,
          office_area: false,
          bathroom_area: false,
          common_areas: false,
          encapsulation_machine: false,
          tea_machine: false,
          packaging_equipment: false,
          scales: false,
          other_equipment: false,
          cleaner_name: '',
          cleaning_products_used: '',
          cleaning_quality: '',
          inspection_notes: ''
        };
        break;

      case 'RECEIVING':
        details = {
          supplier_name: '',
          delivery_note_number: '',
          received_by_name: '',
          quantity_ordered: 0,
          quantity_received: 0,
          quantity_unit: 'kg',
          condition_on_arrival: '',
          packaging_condition: '',
          quality_inspection: false,
          quality_approved: false,
          storage_location: ''
        };
        break;

      case 'RETURNS':
        details = {
          client_id: null,
          client_name: '',
          returned_quantity: 0,
          quantity_unit: 'unidades',
          return_reason: '',
          return_category: 'QUALITY_ISSUE',
          return_status: 'PENDING',
          condition_returned: '',
          action_taken: '',
          refund_amount: 0,
          credit_issued: false,
          processed_by: null,
          processed_date: null
        };
        break;

      case 'MAINTENANCE':
        details = {
          equipment_name: '',
          equipment_code: '',
          maintenance_type: 'PREVENTIVE',
          technician_name: '',
          technician_company: '',
          description: '',
          parts_replaced: '',
          materials_used: '',
          start_time: '',
          end_time: '',
          downtime_hours: 0,
          condition_before: '',
          condition_after: '',
          is_operational: true,
          next_maintenance_date: '',
          maintenance_interval_days: 30,
          labor_cost: 0,
          parts_cost: 0,
          total_cost: 0
        };
        break;

      default:
        details = {};
    }

    setFormData(prev => ({
      ...prev,
      production_process: {
        ...prev.production_process,
        process_type: type
      },
      process_details: details
    }));
  };

  const handleProcessTypeChange = (type) => {
    setProcessType(type);
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));

    // Limpiar errores de validación
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      process_details: {
        ...prev.process_details,
        [field]: value
      }
    }));
  };

  const calculateDuration = () => {
    const { start_time, end_time } = formData.production_process;
    if (start_time && end_time) {
      const [startH, startM] = start_time.split(':').map(Number);
      const [endH, endM] = end_time.split(':').map(Number);
      const duration = (endH * 60 + endM) - (startH * 60 + startM);

      if (duration > 0) {
        handleInputChange('production_process', 'duration_minutes', duration);
      }
    }
  };

  useEffect(() => {
    calculateDuration();
  }, [formData.production_process.start_time, formData.production_process.end_time]);

  const validateForm = () => {
    const validation = procesosService.validateProcessData(processType, formData);

    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return false;
    }

    setValidationErrors({});
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      setError('Por favor corrija los errores en el formulario');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      console.log('Estado formData antes de limpiar:', JSON.stringify(formData, null, 2));

      // Limpiar y convertir valores numéricos
      const cleanedDetails = { ...formData.process_details };

      // Eliminar campos que no pertenecen a process_details
      delete cleanedDetails.id;
      delete cleanedDetails.product_name;
      delete cleanedDetails.process_date;
      delete cleanedDetails.operator_name;
      delete cleanedDetails.batch_number;
      delete cleanedDetails.product_id;
      delete cleanedDetails.supervisor_id;
      delete cleanedDetails.quantity_produced;
      delete cleanedDetails.quantity_units;
      delete cleanedDetails.start_time;
      delete cleanedDetails.end_time;
      delete cleanedDetails.duration_minutes;
      delete cleanedDetails.notes;

      // Convertir strings a números para campos numéricos
      if (processType === 'TEA_PACKING') {
        // Campos requeridos
        cleanedDetails.packed_units = parseInt(cleanedDetails.packed_units) || 0;
        cleanedDetails.packer_name = cleanedDetails.packer_name?.trim() || '';

        // Campos opcionales - eliminar si no tienen valor
        if (cleanedDetails.tea_bags_per_box) {
          cleanedDetails.tea_bags_per_box = parseInt(cleanedDetails.tea_bags_per_box);
        } else {
          delete cleanedDetails.tea_bags_per_box;
        }

        if (cleanedDetails.total_tea_bags) {
          cleanedDetails.total_tea_bags = parseInt(cleanedDetails.total_tea_bags);
        } else {
          delete cleanedDetails.total_tea_bags;
        }

        // Campo opcional company_name - enviar como null si está vacío
        if (!cleanedDetails.company_name?.trim()) {
          cleanedDetails.company_name = null;
        }

        // Eliminar otros campos opcionales vacíos
        if (!cleanedDetails.seal_quality?.trim()) {
          delete cleanedDetails.seal_quality;
        }
        if (!cleanedDetails.package_appearance?.trim()) {
          delete cleanedDetails.package_appearance;
        }
      }

      if (processType === 'PACKAGING') {
        // Campos requeridos - convertir a números
        cleanedDetails.packed_units = parseInt(cleanedDetails.packed_units) || 0;
        cleanedDetails.units_per_container = parseInt(cleanedDetails.units_per_container) || 0;
        cleanedDetails.total_containers = parseInt(cleanedDetails.total_containers) || 0;
        cleanedDetails.packer_name = cleanedDetails.packer_name?.trim() || '';

        // Campo opcional company_name - enviar como null si está vacío
        if (!cleanedDetails.company_name?.trim()) {
          cleanedDetails.company_name = null;
        }

        // Eliminar otros campos opcionales vacíos que no son parte de la API
        if (!cleanedDetails.packaging_type?.trim()) {
          delete cleanedDetails.packaging_type;
        }
        if (!cleanedDetails.package_condition?.trim()) {
          delete cleanedDetails.package_condition;
        }
        if (!cleanedDetails.labeling_quality?.trim()) {
          delete cleanedDetails.labeling_quality;
        }
      }

      if (processType === 'MAINTENANCE') {
        // Campos requeridos
        cleanedDetails.equipment_name = cleanedDetails.equipment_name?.trim() || '';
        cleanedDetails.maintenance_type = cleanedDetails.maintenance_type || 'PREVENTIVE';
        cleanedDetails.technician_name = cleanedDetails.technician_name?.trim() || '';
        cleanedDetails.parts_replaced = cleanedDetails.parts_replaced?.trim() || '';
        cleanedDetails.total_cost = parseFloat(cleanedDetails.total_cost) || 0;
        cleanedDetails.is_operational = cleanedDetails.is_operational !== undefined ? cleanedDetails.is_operational : true;

        // Eliminar campos opcionales vacíos
        if (!cleanedDetails.equipment_code?.trim()) {
          delete cleanedDetails.equipment_code;
        }
        if (!cleanedDetails.technician_company?.trim()) {
          delete cleanedDetails.technician_company;
        }
        if (!cleanedDetails.description?.trim()) {
          delete cleanedDetails.description;
        }
        if (!cleanedDetails.materials_used?.trim()) {
          delete cleanedDetails.materials_used;
        }
        if (!cleanedDetails.start_time) {
          delete cleanedDetails.start_time;
        }
        if (!cleanedDetails.end_time) {
          delete cleanedDetails.end_time;
        }
        if (!cleanedDetails.downtime_hours || cleanedDetails.downtime_hours === 0) {
          delete cleanedDetails.downtime_hours;
        }
        if (!cleanedDetails.condition_before?.trim()) {
          delete cleanedDetails.condition_before;
        }
        if (!cleanedDetails.condition_after?.trim()) {
          delete cleanedDetails.condition_after;
        }
        if (!cleanedDetails.next_maintenance_date) {
          delete cleanedDetails.next_maintenance_date;
        }
        if (!cleanedDetails.maintenance_interval_days || cleanedDetails.maintenance_interval_days === 0) {
          delete cleanedDetails.maintenance_interval_days;
        }
        if (!cleanedDetails.labor_cost || cleanedDetails.labor_cost === 0) {
          delete cleanedDetails.labor_cost;
        }
        if (!cleanedDetails.parts_cost || cleanedDetails.parts_cost === 0) {
          delete cleanedDetails.parts_cost;
        }
      }

      // Construir production_process con todos los campos requeridos
      const productionProcess = {
        process_type: processType,  // ✅ Incluir process_type aquí también
        product_name: formData.production_process.product_name?.trim() || '',
        process_date: formData.production_process.process_date,
        operator_name: formData.production_process.operator_name?.trim() || '',  // ✅ Siempre incluir (vacío = "")
        notes: formData.production_process.notes?.trim() || ''  // ✅ Siempre incluir (vacío = "")
      };

      // Agregar batch_number solo si tiene valor
      if (formData.production_process.batch_number?.trim()) {
        productionProcess.batch_number = formData.production_process.batch_number.trim();
      }

      // Agregar otros campos opcionales solo si tienen valor
      if (formData.production_process.start_time) {
        productionProcess.start_time = formData.production_process.start_time;
      }
      if (formData.production_process.end_time) {
        productionProcess.end_time = formData.production_process.end_time;
      }
      if (formData.production_process.product_id) {
        productionProcess.product_id = formData.production_process.product_id;
      }
      if (formData.production_process.supervisor_id) {
        productionProcess.supervisor_id = formData.production_process.supervisor_id;
      }

      const submitData = {
        process_type: processType,
        production_process: productionProcess,
        process_details: cleanedDetails
      };

      console.log('=== DATOS FINALES A ENVIAR ===');
      console.log(JSON.stringify(submitData, null, 2));
      console.log('==============================');

      let result;
      if (isEditing) {
        result = await procesosService.updateProceso(id, submitData);
      } else {
        result = await procesosService.createProceso(submitData);
      }

      if (result.success) {
        navigate('/system/procesos');
      } else {
        // Manejar errores de validación del backend
        if (result.errors && Array.isArray(result.errors)) {
          const errorMessages = result.errors.map(err => {
            const field = err.loc ? err.loc[err.loc.length - 1] : 'campo';
            return `${field}: ${err.msg}`;
          }).join('\n');
          setError(`Errores de validación:\n${errorMessages}`);
        } else {
          setError(result.message || 'Error al guardar el proceso');
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      // Capturar errores de validación del axios
      if (error.response?.data?.detail) {
        const details = error.response.data.detail;
        if (Array.isArray(details)) {
          const errorMessages = details.map(err => {
            const field = err.loc ? err.loc[err.loc.length - 1] : 'campo';
            return `• ${field}: ${err.msg}`;
          }).join('\n');
          setError(`Errores de validación:\n${errorMessages}`);
        } else {
          setError(details);
        }
      } else {
        setError('Error de conexión al guardar el proceso');
      }
      console.error('Error completo:', error);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/system/procesos');
  };

  if (isLoading && isEditing) {
    return <LoadingGrid message="Cargando proceso..." />;
  }

  return (
    <div className="factura-form-page">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={handleCancel}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          {isEditing ? 'Editar Proceso' : 'Registrar Nuevo Proceso'}
        </h1>

        <div className="header-actions">
          <button className="btn btn--outline" onClick={handleCancel}>
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isLoading || !processType}
          >
            <FiSave size={16} />
            {isEditing ? 'Actualizar' : 'Registrar Proceso'}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ whiteSpace: 'pre-line' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Validation errors */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="alert alert--warning">
          <strong>Corrija los siguientes errores:</strong>
          <ul style={{ marginTop: '8px', marginBottom: 0 }}>
            {Object.entries(validationErrors).map(([key, value]) => (
              <li key={key}>{value}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="factura-form">
        {/* Card 1: Selección de Tipo de Proceso */}
        <div className="invoice-header-card">
          <div className="invoice-header-card__left">
            <div className="invoice-icon-circle">
              <FiLayers size={32} />
            </div>
            <div className="invoice-header-info">
              <h3 className="invoice-header-title">Tipo de Proceso</h3>
              <p className="invoice-header-subtitle">
                Seleccione el tipo de proceso a registrar
              </p>
            </div>
          </div>

          <div className="invoice-header-card__grid">
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiLayers size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Tipo de Proceso *</label>
                <select
                  className="invoice-field-input"
                  value={processType}
                  onChange={(e) => handleProcessTypeChange(e.target.value)}
                  required
                  disabled={isEditing}
                >
                  <option value="">Seleccionar tipo...</option>
                  {processTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Información General del Proceso */}
        {processType && (
          <div className="invoice-header-card">
            <div className="invoice-header-card__left">
              <div className="invoice-icon-circle">
                <FiPackage size={32} />
              </div>
              <div className="invoice-header-info">
                <h3 className="invoice-header-title">Información General</h3>
                <p className="invoice-header-subtitle">
                  Datos generales del proceso
                </p>
              </div>
            </div>

            <div className="invoice-header-card__grid">
              {/* Fecha del Proceso */}
              <div className="invoice-field">
                <div className="invoice-field-icon">
                  <FiCalendar size={18} />
                </div>
                <div className="invoice-field-content">
                  <label className="invoice-field-label">Fecha del Proceso *</label>
                  <input
                    type="date"
                    className="invoice-field-input"
                    value={formData.production_process.process_date}
                    onChange={(e) => handleInputChange('production_process', 'process_date', e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Número de Lote */}
              <div className="invoice-field">
                <div className="invoice-field-icon">
                  <FiPackage size={18} />
                </div>
                <div className="invoice-field-content">
                  <label className="invoice-field-label">Número de Lote</label>
                  <input
                    type="text"
                    className="invoice-field-input"
                    value={formData.production_process.batch_number}
                    onChange={(e) => handleInputChange('production_process', 'batch_number', e.target.value)}
                    placeholder="BATCH-2024-001"
                  />
                </div>
              </div>

              {/* Producto (solo si no es CLEANING ni MAINTENANCE) */}
              {processType !== 'CLEANING' && processType !== 'MAINTENANCE' && (
                <div className="invoice-field">
                  <div className="invoice-field-icon">
                    <FiPackage size={18} />
                  </div>
                  <div className="invoice-field-content">
                    <label className="invoice-field-label">Nombre del Producto *</label>
                    <input
                      type="text"
                      className="invoice-field-input"
                      value={formData.production_process.product_name}
                      onChange={(e) => handleInputChange('production_process', 'product_name', e.target.value)}
                      placeholder="Nombre del producto"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Operador */}
              <div className="invoice-field">
                <div className="invoice-field-icon">
                  <FiUser size={18} />
                </div>
                <div className="invoice-field-content">
                  <label className="invoice-field-label">
                    Operador {processType === 'ENCAPSULATION' ? '*' : ''}
                  </label>
                  <input
                    type="text"
                    className={`invoice-field-input ${validationErrors.operator_name ? 'error' : ''}`}
                    value={formData.production_process.operator_name}
                    onChange={(e) => handleInputChange('production_process', 'operator_name', e.target.value)}
                    placeholder="Nombre del operador"
                    required={processType === 'ENCAPSULATION'}
                  />
                  {validationErrors.operator_name && (
                    <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.operator_name}
                    </span>
                  )}
                </div>
              </div>

              {/* Hora de Inicio */}
              <div className="invoice-field">
                <div className="invoice-field-icon">
                  <FiClock size={18} />
                </div>
                <div className="invoice-field-content">
                  <label className="invoice-field-label">Hora de Inicio</label>
                  <input
                    type="time"
                    className="invoice-field-input"
                    value={formData.production_process.start_time}
                    onChange={(e) => handleInputChange('production_process', 'start_time', e.target.value)}
                  />
                </div>
              </div>

              {/* Hora de Fin */}
              <div className="invoice-field">
                <div className="invoice-field-icon">
                  <FiClock size={18} />
                </div>
                <div className="invoice-field-content">
                  <label className="invoice-field-label">Hora de Fin</label>
                  <input
                    type="time"
                    className="invoice-field-input"
                    value={formData.production_process.end_time}
                    onChange={(e) => handleInputChange('production_process', 'end_time', e.target.value)}
                  />
                </div>
              </div>

              {/* Duración (calculada) */}
              <div className="invoice-field">
                <div className="invoice-field-icon">
                  <FiClock size={18} />
                </div>
                <div className="invoice-field-content">
                  <label className="invoice-field-label">Duración (minutos)</label>
                  <input
                    type="number"
                    className="invoice-field-input"
                    value={formData.production_process.duration_minutes}
                    readOnly
                    disabled
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </div>
              </div>

              {/* Notas - Ocupa 2 columnas */}
              <div className="invoice-field invoice-field--full">
                <div className="invoice-field-icon">
                  <FiPackage size={18} />
                </div>
                <div className="invoice-field-content">
                  <label className="invoice-field-label">Notas</label>
                  <textarea
                    className="invoice-field-input invoice-field-textarea"
                    value={formData.production_process.notes}
                    onChange={(e) => handleInputChange('production_process', 'notes', e.target.value)}
                    placeholder="Notas adicionales sobre el proceso..."
                    rows="2"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Card 3: Detalles Específicos según Tipo */}
        {processType && (
          <ProcessDetailsCard
            processType={processType}
            formData={formData}
            handleInputChange={handleInputChange}
            handleCheckboxChange={handleCheckboxChange}
            clientes={clientes}
            validationErrors={validationErrors}
          />
        )}
      </form>
    </div>
  );
};

// Componente para renderizar detalles específicos según el tipo
const ProcessDetailsCard = ({
  processType,
  formData,
  handleInputChange,
  handleCheckboxChange,
  clientes,
  validationErrors
}) => {
  const details = formData.process_details;

  const renderEncapsulationDetails = () => (
    <div className="invoice-header-card__grid">
      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Peso Neto por Cápsula (g)</label>
          <input
            type="number"
            step="0.01"
            className="invoice-field-input"
            value={details.net_weight_per_capsule || ''}
            onChange={(e) => handleInputChange('process_details', 'net_weight_per_capsule', parseFloat(e.target.value))}
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Peso Real por Cápsula (g)</label>
          <input
            type="number"
            step="0.01"
            className="invoice-field-input"
            value={details.actual_weight_per_capsule || ''}
            onChange={(e) => handleInputChange('process_details', 'actual_weight_per_capsule', parseFloat(e.target.value))}
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Botes Producidos</label>
          <input
            type="number"
            className="invoice-field-input"
            value={details.bottles_produced || ''}
            onChange={(e) => handleInputChange('process_details', 'bottles_produced', parseInt(e.target.value))}
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Cápsulas por Bote</label>
          <input
            type="number"
            className="invoice-field-input"
            value={details.capsules_per_bottle || ''}
            onChange={(e) => handleInputChange('process_details', 'capsules_per_bottle', parseInt(e.target.value))}
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">% Desperdicio</label>
          <input
            type="number"
            step="0.1"
            className="invoice-field-input"
            value={details.waste_percentage || ''}
            onChange={(e) => handleInputChange('process_details', 'waste_percentage', parseFloat(e.target.value))}
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">% Eficiencia</label>
          <input
            type="number"
            step="0.1"
            className="invoice-field-input"
            value={details.efficiency_percentage || ''}
            onChange={(e) => handleInputChange('process_details', 'efficiency_percentage', parseFloat(e.target.value))}
          />
        </div>
      </div>

      <div className="invoice-field invoice-field--full">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Apariencia de Cápsulas</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.capsule_appearance || ''}
            onChange={(e) => handleInputChange('process_details', 'capsule_appearance', e.target.value)}
            placeholder="Buena, Excelente, etc."
          />
        </div>
      </div>
    </div>
  );

  const renderTeaPackingDetails = () => {
    // Calcular total de bolsitas automáticamente
    const calculateTotalTeaBags = () => {
      const packedUnits = parseInt(details.packed_units) || 0;
      const teaBagsPerBox = parseInt(details.tea_bags_per_box) || 0;
      const total = packedUnits * teaBagsPerBox;
      if (total !== (parseInt(details.total_tea_bags) || 0)) {
        handleInputChange('process_details', 'total_tea_bags', total);
      }
    };

    React.useEffect(() => {
      calculateTotalTeaBags();
    }, [details.packed_units, details.tea_bags_per_box]);

    return (
      <div className="invoice-header-card__grid">
        <div className="invoice-field">
          <div className="invoice-field-icon"><FiPackage size={18} /></div>
          <div className="invoice-field-content">
            <label className="invoice-field-label">Unidades Empacadas *</label>
            <input
              type="number"
              className={`invoice-field-input ${validationErrors.packed_units ? 'error' : ''}`}
              value={details.packed_units || ''}
              onChange={(e) => handleInputChange('process_details', 'packed_units', parseInt(e.target.value) || 0)}
              required
              min="1"
            />
            {validationErrors.packed_units && (
              <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {validationErrors.packed_units}
              </span>
            )}
          </div>
        </div>

        <div className="invoice-field">
          <div className="invoice-field-icon"><FiPackage size={18} /></div>
          <div className="invoice-field-content">
            <label className="invoice-field-label">Bolsitas por Caja</label>
            <input
              type="number"
              className="invoice-field-input"
              value={details.tea_bags_per_box || ''}
              onChange={(e) => handleInputChange('process_details', 'tea_bags_per_box', parseInt(e.target.value) || 0)}
              min="1"
            />
          </div>
        </div>

        <div className="invoice-field">
          <div className="invoice-field-icon"><FiPackage size={18} /></div>
          <div className="invoice-field-content">
            <label className="invoice-field-label">Total Bolsitas (calculado)</label>
            <input
              type="number"
              className="invoice-field-input"
              value={details.total_tea_bags || 0}
              readOnly
              disabled
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </div>
        </div>

        <div className="invoice-field">
          <div className="invoice-field-icon"><FiUser size={18} /></div>
          <div className="invoice-field-content">
            <label className="invoice-field-label">Nombre del Empacador *</label>
            <input
              type="text"
              className={`invoice-field-input ${validationErrors.packer_name ? 'error' : ''}`}
              value={details.packer_name || ''}
              onChange={(e) => handleInputChange('process_details', 'packer_name', e.target.value)}
              required
            />
            {validationErrors.packer_name && (
              <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {validationErrors.packer_name}
              </span>
            )}
          </div>
        </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Compañía</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.company_name || ''}
            onChange={(e) => handleInputChange('process_details', 'company_name', e.target.value)}
            placeholder="Betel Natural"
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Calidad del Sellado</label>
          <select
            className="invoice-field-input"
            value={details.seal_quality || ''}
            onChange={(e) => handleInputChange('process_details', 'seal_quality', e.target.value)}
          >
            <option value="">Seleccionar...</option>
            <option value="Excelente">Excelente</option>
            <option value="Buena">Buena</option>
            <option value="Regular">Regular</option>
            <option value="Mala">Mala</option>
          </select>
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Apariencia del Empaque</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.package_appearance || ''}
            onChange={(e) => handleInputChange('process_details', 'package_appearance', e.target.value)}
          />
        </div>
      </div>
      </div>
    );
  };

  const renderPackagingDetails = () => (
    <div className="invoice-header-card__grid">
      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Unidades Empacadas *</label>
          <input
            type="number"
            className={`invoice-field-input ${validationErrors.packed_units ? 'error' : ''}`}
            value={details.packed_units || ''}
            onChange={(e) => handleInputChange('process_details', 'packed_units', parseInt(e.target.value) || 0)}
            required
            min="1"
          />
          {validationErrors.packed_units && (
            <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {validationErrors.packed_units}
            </span>
          )}
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Unidades por Contenedor *</label>
          <input
            type="number"
            className={`invoice-field-input ${validationErrors.units_per_container ? 'error' : ''}`}
            value={details.units_per_container || ''}
            onChange={(e) => handleInputChange('process_details', 'units_per_container', parseInt(e.target.value) || 0)}
            required
            min="1"
          />
          {validationErrors.units_per_container && (
            <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {validationErrors.units_per_container}
            </span>
          )}
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Total Contenedores *</label>
          <input
            type="number"
            className={`invoice-field-input ${validationErrors.total_containers ? 'error' : ''}`}
            value={details.total_containers || ''}
            onChange={(e) => handleInputChange('process_details', 'total_containers', parseInt(e.target.value) || 0)}
            required
            min="1"
          />
          {validationErrors.total_containers && (
            <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {validationErrors.total_containers}
            </span>
          )}
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiUser size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Nombre del Empacador *</label>
          <input
            type="text"
            className={`invoice-field-input ${validationErrors.packer_name ? 'error' : ''}`}
            value={details.packer_name || ''}
            onChange={(e) => handleInputChange('process_details', 'packer_name', e.target.value)}
            required
          />
          {validationErrors.packer_name && (
            <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {validationErrors.packer_name}
            </span>
          )}
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Compañía</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.company_name || ''}
            onChange={(e) => handleInputChange('process_details', 'company_name', e.target.value)}
            placeholder="Betel Natural"
          />
        </div>
      </div>
    </div>
  );

  const renderCleaningDetails = () => (
    <>
      <div className="invoice-header-card__grid">
        <div className="invoice-field invoice-field--full">
          <div className="invoice-field-icon"><FiCheckSquare size={18} /></div>
          <div className="invoice-field-content">
            <label className="invoice-field-label">Áreas Limpiadas</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={details.production_area || false}
                  onChange={(e) => handleCheckboxChange('production_area', e.target.checked)}
                />
                Área de Producción
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={details.packaging_area || false}
                  onChange={(e) => handleCheckboxChange('packaging_area', e.target.checked)}
                />
                Área de Empaque
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={details.warehouse_area || false}
                  onChange={(e) => handleCheckboxChange('warehouse_area', e.target.checked)}
                />
                Almacén
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={details.office_area || false}
                  onChange={(e) => handleCheckboxChange('office_area', e.target.checked)}
                />
                Oficinas
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={details.bathroom_area || false}
                  onChange={(e) => handleCheckboxChange('bathroom_area', e.target.checked)}
                />
                Baños
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={details.common_areas || false}
                  onChange={(e) => handleCheckboxChange('common_areas', e.target.checked)}
                />
                Áreas Comunes
              </label>
            </div>
          </div>
        </div>

        <div className="invoice-field invoice-field--full">
          <div className="invoice-field-icon"><FiCheckSquare size={18} /></div>
          <div className="invoice-field-content">
            <label className="invoice-field-label">Equipos Limpiados</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={details.encapsulation_machine || false}
                  onChange={(e) => handleCheckboxChange('encapsulation_machine', e.target.checked)}
                />
                Máquina Encapsuladora
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={details.tea_machine || false}
                  onChange={(e) => handleCheckboxChange('tea_machine', e.target.checked)}
                />
                Máquina de Tés
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={details.packaging_equipment || false}
                  onChange={(e) => handleCheckboxChange('packaging_equipment', e.target.checked)}
                />
                Equipo de Empaque
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={details.scales || false}
                  onChange={(e) => handleCheckboxChange('scales', e.target.checked)}
                />
                Básculas
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={details.other_equipment || false}
                  onChange={(e) => handleCheckboxChange('other_equipment', e.target.checked)}
                />
                Otros Equipos
              </label>
            </div>
          </div>
        </div>

        <div className="invoice-field">
          <div className="invoice-field-icon"><FiUser size={18} /></div>
          <div className="invoice-field-content">
            <label className="invoice-field-label">Responsable de Limpieza</label>
            <input
              type="text"
              className="invoice-field-input"
              value={details.cleaner_name || ''}
              onChange={(e) => handleInputChange('process_details', 'cleaner_name', e.target.value)}
            />
          </div>
        </div>

        <div className="invoice-field">
          <div className="invoice-field-icon"><FiPackage size={18} /></div>
          <div className="invoice-field-content">
            <label className="invoice-field-label">Productos Utilizados</label>
            <input
              type="text"
              className="invoice-field-input"
              value={details.cleaning_products_used || ''}
              onChange={(e) => handleInputChange('process_details', 'cleaning_products_used', e.target.value)}
              placeholder="Desinfectante, alcohol, etc."
            />
          </div>
        </div>

        <div className="invoice-field invoice-field--full">
          <div className="invoice-field-icon"><FiPackage size={18} /></div>
          <div className="invoice-field-content">
            <label className="invoice-field-label">Calidad de Limpieza</label>
            <select
              className="invoice-field-input"
              value={details.cleaning_quality || ''}
              onChange={(e) => handleInputChange('process_details', 'cleaning_quality', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="Excelente">Excelente</option>
              <option value="Buena">Buena</option>
              <option value="Regular">Regular</option>
              <option value="Mala">Mala</option>
            </select>
          </div>
        </div>
      </div>
    </>
  );

  const renderReturnsDetails = () => (
    <div className="invoice-header-card__grid">
      <div className="invoice-field">
        <div className="invoice-field-icon"><FiUser size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Cliente *</label>
          <select
            className="invoice-field-input"
            value={details.client_id || ''}
            onChange={(e) => {
              const clientId = parseInt(e.target.value);
              const client = clientes.find(c => c.id === clientId);
              handleInputChange('process_details', 'client_id', clientId);
              if (client) {
                handleInputChange('process_details', 'client_name', client.nombreNegocio || client.company_name);
              }
            }}
            required
          >
            <option value="">Seleccionar cliente...</option>
            {clientes.map(cliente => (
              <option key={cliente.id} value={cliente.id}>
                {cliente.nombreNegocio || cliente.company_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Cantidad Retornada</label>
          <input
            type="number"
            className="invoice-field-input"
            value={details.returned_quantity || ''}
            onChange={(e) => handleInputChange('process_details', 'returned_quantity', parseInt(e.target.value))}
          />
        </div>
      </div>

      <div className="invoice-field invoice-field--full">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Motivo del Retorno</label>
          <textarea
            className="invoice-field-input invoice-field-textarea"
            value={details.return_reason || ''}
            onChange={(e) => handleInputChange('process_details', 'return_reason', e.target.value)}
            rows="2"
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Condición del Producto</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.condition_returned || ''}
            onChange={(e) => handleInputChange('process_details', 'condition_returned', e.target.value)}
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Monto de Reembolso</label>
          <input
            type="number"
            step="0.01"
            className="invoice-field-input"
            value={details.refund_amount || ''}
            onChange={(e) => handleInputChange('process_details', 'refund_amount', parseFloat(e.target.value))}
          />
        </div>
      </div>
    </div>
  );

  const renderMaintenanceDetails = () => (
    <div className="invoice-header-card__grid">
      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Nombre del Equipo *</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.equipment_name || ''}
            onChange={(e) => handleInputChange('process_details', 'equipment_name', e.target.value)}
            placeholder="Ej: Encapsuladora Principal"
            required
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Código del Equipo</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.equipment_code || ''}
            onChange={(e) => handleInputChange('process_details', 'equipment_code', e.target.value)}
            placeholder="EQ-001"
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Tipo de Mantenimiento *</label>
          <select
            className="invoice-field-input"
            value={details.maintenance_type || 'PREVENTIVE'}
            onChange={(e) => handleInputChange('process_details', 'maintenance_type', e.target.value)}
            required
          >
            <option value="PREVENTIVE">Preventivo</option>
            <option value="CORRECTIVE">Correctivo</option>
            <option value="PREDICTIVE">Predictivo</option>
            <option value="EMERGENCY">Emergencia</option>
          </select>
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiUser size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Nombre del Técnico *</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.technician_name || ''}
            onChange={(e) => handleInputChange('process_details', 'technician_name', e.target.value)}
            placeholder="Nombre del técnico"
            required
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Compañía del Técnico</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.technician_company || ''}
            onChange={(e) => handleInputChange('process_details', 'technician_company', e.target.value)}
            placeholder="Empresa externa"
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiClock size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Horas de Inactividad</label>
          <input
            type="number"
            step="0.5"
            className="invoice-field-input"
            value={details.downtime_hours || ''}
            onChange={(e) => handleInputChange('process_details', 'downtime_hours', parseFloat(e.target.value))}
          />
        </div>
      </div>

      <div className="invoice-field invoice-field--full">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Descripción del Mantenimiento</label>
          <textarea
            className="invoice-field-input invoice-field-textarea"
            value={details.description || ''}
            onChange={(e) => handleInputChange('process_details', 'description', e.target.value)}
            placeholder="Descripción detallada del trabajo realizado..."
            rows="2"
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Partes Reemplazadas</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.parts_replaced || ''}
            onChange={(e) => handleInputChange('process_details', 'parts_replaced', e.target.value)}
            placeholder="Filtros, rodamientos, etc."
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Materiales Utilizados</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.materials_used || ''}
            onChange={(e) => handleInputChange('process_details', 'materials_used', e.target.value)}
            placeholder="Aceites, lubricantes, etc."
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Condición Antes</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.condition_before || ''}
            onChange={(e) => handleInputChange('process_details', 'condition_before', e.target.value)}
            placeholder="Estado del equipo antes"
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Condición Después</label>
          <input
            type="text"
            className="invoice-field-input"
            value={details.condition_after || ''}
            onChange={(e) => handleInputChange('process_details', 'condition_after', e.target.value)}
            placeholder="Estado del equipo después"
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiCalendar size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Próximo Mantenimiento</label>
          <input
            type="date"
            className="invoice-field-input"
            value={details.next_maintenance_date || ''}
            onChange={(e) => handleInputChange('process_details', 'next_maintenance_date', e.target.value)}
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiClock size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Intervalo (días)</label>
          <input
            type="number"
            className="invoice-field-input"
            value={details.maintenance_interval_days || ''}
            onChange={(e) => handleInputChange('process_details', 'maintenance_interval_days', parseInt(e.target.value))}
            placeholder="30"
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Costo Mano de Obra</label>
          <input
            type="number"
            step="0.01"
            className="invoice-field-input"
            value={details.labor_cost || ''}
            onChange={(e) => handleInputChange('process_details', 'labor_cost', parseFloat(e.target.value))}
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Costo de Partes</label>
          <input
            type="number"
            step="0.01"
            className="invoice-field-input"
            value={details.parts_cost || ''}
            onChange={(e) => handleInputChange('process_details', 'parts_cost', parseFloat(e.target.value))}
          />
        </div>
      </div>

      <div className="invoice-field">
        <div className="invoice-field-icon"><FiPackage size={18} /></div>
        <div className="invoice-field-content">
          <label className="invoice-field-label">Costo Total</label>
          <input
            type="number"
            step="0.01"
            className="invoice-field-input"
            value={details.total_cost || ''}
            onChange={(e) => handleInputChange('process_details', 'total_cost', parseFloat(e.target.value))}
          />
        </div>
      </div>

      <div className="invoice-field invoice-field--full">
        <div className="invoice-field-icon"><FiCheckSquare size={18} /></div>
        <div className="invoice-field-content">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
            <input
              type="checkbox"
              checked={details.is_operational || false}
              onChange={(e) => handleCheckboxChange('is_operational', e.target.checked)}
            />
            Equipo Operacional Después del Mantenimiento
          </label>
        </div>
      </div>
    </div>
  );

  // Renderizar según tipo
  let content = null;
  let title = '';
  let subtitle = '';

  switch (processType) {
    case 'ENCAPSULATION':
      title = 'Detalles de Encapsulado';
      subtitle = 'Información específica del proceso de encapsulado';
      content = renderEncapsulationDetails();
      break;
    case 'TEA_PACKING':
      title = 'Detalles de Empaque de Tés';
      subtitle = 'Información específica del empaque de tés';
      content = renderTeaPackingDetails();
      break;
    case 'PACKAGING':
      title = 'Detalles de Empacado';
      subtitle = 'Información específica del proceso de empacado';
      content = renderPackagingDetails();
      break;
    case 'CLEANING':
      title = 'Detalles de Limpieza';
      subtitle = 'Áreas y equipos limpiados';
      content = renderCleaningDetails();
      break;
    case 'RETURNS':
      title = 'Detalles del Retorno';
      subtitle = 'Información del retorno de producto';
      content = renderReturnsDetails();
      break;
    case 'MAINTENANCE':
      title = 'Detalles de Mantenimiento';
      subtitle = 'Información del mantenimiento del equipo';
      content = renderMaintenanceDetails();
      break;
    default:
      return null;
  }

  return (
    <div className="invoice-header-card">
      <div className="invoice-header-card__left">
        <div className="invoice-icon-circle">
          <FiCheckSquare size={32} />
        </div>
        <div className="invoice-header-info">
          <h3 className="invoice-header-title">{title}</h3>
          <p className="invoice-header-subtitle">{subtitle}</p>
        </div>
      </div>
      {content}
    </div>
  );
};

export default ProcesoForm;
